/**
 * areaForPath — the ONE client-side route → engagement-AREA tag mapper (ANL-PR3).
 *
 * THE WRITER of the `<section>:<detail>` tag the engagement beacon stamps on
 * every /telemetry/session flush. It MUST stay in lock-step with the backend
 * READER, backend/app/analytics_areas.py `parse_area` (the ANL-PR2 aggregation),
 * so a tag this module emits lands in the section the founder's roll-ups expect.
 * The taxonomy (kept identical to that module's docstring):
 *
 *     /advisor              -> admissions:advisor
 *     /advisor/shortlist    -> admissions:shortlist
 *     /advisor/profile      -> admissions:profile
 *     /career  (index)      -> career:matches
 *     /career/insights      -> career:insights
 *     /career/explore       -> career:explore
 *     /career/assessments   -> career:checkins
 *     /career/interview     -> career:interview
 *     /career/shortlist     -> career:shortlist
 *     /career/advisor       -> career:advisor
 *     /career/profile       -> career:profile
 *     /learn, /chat, /paths -> learn   (or learn:<Subject> when readily known)
 *     /prep*                -> prep:   (or prep:<TestType> when readily known)
 *     anything else         -> undefined  (count the session, but no area tag)
 *
 * WHY prep's base tag is "prep:" and learn's is "learn" (NOT "prep")
 * -----------------------------------------------------------------
 * `parse_area` treats a tag with NO ':' as a BARE LEARN SUBJECT — so the string
 * "learn" round-trips to (section=learn, detail=None) which is exactly right for
 * "learn section, unknown subject". But the string "prep" (no ':') ALSO parses as
 * a learn subject literally NAMED "prep" — it would be MISCLASSIFIED out of the
 * prep section entirely. The only tag that lands in the prep section with no
 * detail is a NAMESPACED one: "prep:" → (section=prep, detail=None). So the
 * prep base tag is "prep:" (empty detail), the direct analogue of bare "learn".
 * Verified against the live `parse_area` (see the ANL-PR3 report). The asymmetry
 * lives in the backend reader; this comment is the reason the two constants below
 * differ, so a future edit doesn't "simplify" prep's base to bare "prep" and
 * silently move all untyped prep time into the learn bucket.
 *
 * SUBJECT HINTS (the optional enrichment): the beacon mounts high in the app and
 * knows only the ROUTE — it does NOT know which Subject / TestType the learn or
 * prep page has loaded. So those pages may register a short, NON-PII label via
 * `setAreaSubjectHint(section, label)` (e.g. the path's subject "Algebra", the
 * prep test type "SAT"). When a hint is present for the section the mapper
 * resolves to, it is composed into "<section>:<label>"; absent, the base tag is
 * used. A hint is a CURRICULUM label (subject/test name), never free user text —
 * the same privacy rule the backend documents for the open subject column.
 */

// The known section prefixes (mirror analytics_areas.py SECTION_* constants).
export const SECTION_LEARN = 'learn';
export const SECTION_PREP = 'prep';
export const SECTION_ADMISSIONS = 'admissions';
export const SECTION_CAREER = 'career';

// Max stored subject length on the backend (schemas.py: max_length=64). A hint
// is truncated to this so a composed tag can never 422 the beacon.
const MAX_TAG_LEN = 64;

// ── Subject-hint registry (module-level, set by the learn/prep pages) ─────────
// Only `learn` and `prep` carry per-subject hints (admissions/career details are
// fully route-derived). Keyed by section so a stale learn hint can't leak onto a
// prep tag. A hint is cleared by passing a falsy label (page unmount / unknown).
const _subjectHints = { [SECTION_LEARN]: null, [SECTION_PREP]: null };

/**
 * Register (or clear) the curriculum subject/test-type hint for a section.
 *
 * `section` must be SECTION_LEARN or SECTION_PREP (others are route-derived and
 * ignored). `label` is a short curriculum label (subject name / test type); a
 * falsy label CLEARS the hint. The label is sanitized to a single bounded line
 * (newlines/control chars stripped, trimmed, truncated) so it stays a clean,
 * non-PII tag even if a caller passes something unexpected. No-op + safe for any
 * input — telemetry must never throw into the UI.
 */
export function setAreaSubjectHint(section, label) {
  if (section !== SECTION_LEARN && section !== SECTION_PREP) return;
  const clean = _sanitizeLabel(label);
  _subjectHints[section] = clean || null;
}

/** Clear ALL subject hints (e.g. on logout, so a new user starts clean). */
export function clearAreaSubjectHints() {
  _subjectHints[SECTION_LEARN] = null;
  _subjectHints[SECTION_PREP] = null;
}

function _sanitizeLabel(label) {
  if (typeof label !== 'string') return '';
  // Collapse any whitespace/control runs to single spaces, trim, then bound the
  // length so "<section>:<label>" can't exceed the backend's 64-char column.
  const oneLine = label.replace(/\s+/g, ' ').trim();
  if (!oneLine) return '';
  // Reserve room for the longest HINTED section prefix + ':'. Only learn/prep
  // carry hints; "learn:" (6) is longer than "prep:" (5), so reserve for learn
  // — slicing to (64 - 6) keeps even a learn-tagged label within the 64-char
  // backend column. (Under-reserving by using "prep:" would let "learn:<58>"
  // reach 65 and 422 the beacon.)
  return oneLine.slice(0, MAX_TAG_LEN - (SECTION_LEARN.length + 1));
}

/**
 * Map a router pathname to its engagement-area tag (or `undefined`).
 *
 * PURE except for reading the module-level subject hints (which only affect the
 * learn/prep branches). Returns `undefined` for any route outside the taxonomy —
 * the caller still counts the session (active_ms), it just omits the `subject`.
 * Total over any input: a null/empty/garbage path → undefined, never throws.
 *
 * Matching is by path SEGMENT (not substring) so "/careers-fair" can't be
 * mistaken for the career section. Trailing slashes and query/hash are ignored.
 */
export function areaForPath(pathname) {
  const segments = _segments(pathname);
  if (segments.length === 0) return undefined;
  const [head, sub] = segments;

  if (head === 'advisor') {
    // Admissions Advisor section. /advisor (chat) is the advisor surface; the
    // shortlist + profile sub-routes have their own details.
    if (sub === 'shortlist') return 'admissions:shortlist';
    if (sub === 'profile') return 'admissions:profile';
    return 'admissions:advisor';
  }

  if (head === 'career') {
    // Career Discovery section. The index (/career, no sub-route) is the
    // ranked-matches surface; each sub-route maps to its own detail. An unknown
    // future career sub-route falls back to the section's matches surface rather
    // than dropping the tag — it still rolls up under career.
    switch (sub) {
      case undefined: return 'career:matches';
      case 'insights': return 'career:insights';
      case 'explore': return 'career:explore';
      case 'assessments': return 'career:checkins';
      case 'interview': return 'career:interview';
      case 'shortlist': return 'career:shortlist';
      case 'advisor': return 'career:advisor';
      case 'profile': return 'career:profile';
      default: return 'career:matches';
    }
  }

  // Learning / tutoring surfaces share the learn section. /learn (the exercise
  // layout), /chat (the tutor), /paths (path picker) are all "learning time".
  if (head === 'learn' || head === 'chat' || head === 'paths') {
    const hint = _subjectHints[SECTION_LEARN];
    // Bare "learn" round-trips to (section=learn, detail=None) — correct for an
    // unknown subject. With a hint we enrich to "learn:<Subject>".
    return hint ? `${SECTION_LEARN}:${hint}` : SECTION_LEARN;
  }

  // Prep surfaces (/prep, /prep/:id, /prep/upload) share the prep section.
  if (head === 'prep') {
    const hint = _subjectHints[SECTION_PREP];
    // NOTE the trailing ':' on the base tag — bare "prep" misclassifies as a
    // learn subject (see the file header); "prep:" is the prep section with no
    // detail. With a hint we enrich to "prep:<TestType>".
    return hint ? `${SECTION_PREP}:${hint}` : `${SECTION_PREP}:`;
  }

  // Outside the taxonomy (e.g. /progress, /family, /classrooms, /admin,
  // /privacy, "/") — count the session, but emit NO area tag.
  return undefined;
}

/**
 * Split a pathname into lower-cased path segments, dropping empties (leading/
 * trailing slashes, doubled slashes) and any query/hash. Total over junk input.
 */
function _segments(pathname) {
  if (typeof pathname !== 'string' || !pathname) return [];
  // Strip query + hash, then split on '/', drop empty tokens, lower-case.
  const pathOnly = pathname.split(/[?#]/, 1)[0];
  return pathOnly
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

export default areaForPath;
